import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Phone, Video } from "lucide-react";
import { ReactComponent as GoogleLogo } from "../../../Assets/icons/logo/google.svg";
import { ReactComponent as AppleLogo } from "../../../Assets/icons/logo/apple-logo.svg";
import { ReactComponent as MicrosoftLogo } from "../../../Assets/icons/logo/microsoftLogo.svg";
import {
  Hero, Globe, Title, Subtitle, Fields, InputRow, TextInput, PrimaryBtn, Hint, Divider, ErrorText, TermsFooter
} from "../shared/AuthUI";
import { isValidEmail } from "../../../Helpers/emailValidation";
import { useTranslate } from "@tolgee/react";

const MainStep = ({
  email,
  setEmail,
  onEmailContinue,
  emailError,
  onLoginGoogleSubmit,
  onLoginMicrosoftSubmit,
  onLoginAppleSubmit,
  onPhoneClick,
  isDesktop,
}) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [focused, setFocused] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isValidEmail(email)) {
      onEmailContinue();
    }
  };

  return (
    <>
      <Hero>
        <Globe>✨🌐✨</Globe>
        <Title>{t("authentication.main.title")}</Title>
        <Subtitle>{t("authentication.main.subtitle")}</Subtitle>
      </Hero>

      <Fields>
        <InputRow focused={focused}>
          <TextInput
            type="email"
            placeholder={t("settings.enter_email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
            autoComplete="email"
          />
        </InputRow>

        {emailError && <ErrorText>{emailError}</ErrorText>}

        <PrimaryBtn disabled={!isValidEmail(email)} onClick={onEmailContinue}>
          {t("general.continue")}
        </PrimaryBtn>

        <Hint>{t("authentication.main.hint")}</Hint>
      </Fields>

      <Divider>{t("login.continue_with")}</Divider>

      <SSORow>
        {!isDesktop && (
          <>
            <SSOCircle onClick={onLoginGoogleSubmit} title="Google">
              <GoogleLogo width={20} height={20} />
            </SSOCircle>
            <SSOCircle onClick={onLoginAppleSubmit} title="Apple">
              <AppleLogo width={20} height={20} />
            </SSOCircle>
            <SSOCircle onClick={onLoginMicrosoftSubmit} title="Microsoft">
              <MicrosoftLogo width={18} height={18} />
            </SSOCircle>
          </>
        )}
        <SSOCircle onClick={onPhoneClick} title={t("authentication.main.phone_block_title")}>
          <PhoneIcon size={20} />
        </SSOCircle>
      </SSORow>

      {!isDesktop && (
        <>
          <Divider>{t("authentication.main.or")}</Divider>

          <JoinBlock onClick={() => navigate("/meetings/join")}>
            <JoinBlockLeft>
              <JoinIcon size={20} />
            </JoinBlockLeft>
            <JoinBlockText>
              <JoinBlockTitle>{t("authentication.main.join_block_title")}</JoinBlockTitle>
              <JoinBlockSub>{t("authentication.main.join_block_subtitle")}</JoinBlockSub>
            </JoinBlockText>
            <JoinChevron>›</JoinChevron>
          </JoinBlock>
        </>
      )}

      <TermsFooter />
    </>
  );
};

export default MainStep;

const SSORow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const SSOCircle = styled.button`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s, transform 0.15s, box-shadow 0.2s;

  svg { flex-shrink: 0; }

  &:hover {
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.menu.selectedTextColor}22;
    transform: translateY(-2px);
  }
`;

const JoinBlock = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.menu.selectedTextColor}20;
    transform: translateY(-1px);
  }
`;

const JoinBlockLeft = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: ${({ theme }) => theme.menu.selectedTextColor}28;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const JoinIcon = styled(Video)`
  color: ${({ theme }) => theme.menu.selectedTextColor};
`;

const PhoneIcon = styled(Phone)`
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const JoinBlockText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const JoinBlockTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const JoinBlockSub = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1.4;
`;

const JoinChevron = styled.span`
  font-size: 20px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  opacity: 0.6;
  flex-shrink: 0;
`;
